/* eslint function-paren-newline: off */
import moment from 'moment';
import { get } from 'lodash';
import Cookies from 'js-cookie';
import theme from '@ui/theme';
import { isRecord } from './data/contracts';
import type { Locale } from './locale';
import {
  ENTER_KEY,
  ACCOUNT_DROPDOWN,
  SIGN_IN_BUTTON,
  COOKIE_SECURE,
  EXTENSION_REDIRECT_COOKIE_PREFIX,
  BASE_SITE_URL,
} from './constants';

declare const chrome: { runtime?: { sendMessage?: (...args: unknown[]) => unknown } } | undefined;

// // String.Includes polyfill for IE 11
// // https://stackoverflow.com/questions/31221341/ie-does-not-support-includes-method
// if (!String.prototype.includes) {
//   String.prototype.includes = function (search, start) {

//     if (typeof start !== 'number') {
//       start = 0;
//     }

//     if (start + search.length > this.length) {
//       return false;
//     }
//     return this.indexOf(search, start) !== -1;

//   };
// }

// // Array.Includes polyfill for IE 11
// // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/includes
// if (!Array.prototype.includes) {
//   Object.defineProperty(Array.prototype, 'includes', {
//     value(searchElement, fromIndex) {

//       // 1. Let O be ? ToObject(this value).
//       if (this == null) {
//         throw new TypeError('"this" is null or not defined');
//       }

//       const o = Object(this);

//       // 2. Let len be ? ToLength(? Get(O, "length")).
//       const len = o.length >>> 0;

//       // 3. If len is 0, return false.
//       if (len === 0) {
//         return false;
//       }

//       // 4. Let n be ? ToInteger(fromIndex).
//       //    (If fromIndex is undefined, this step produces the value 0.)
//       const n = fromIndex | 0;

//       // 5. If n ≥ 0, then
//       //  a. Let k be n.
//       // 6. Else n < 0,
//       //  a. Let k be len + n.
//       //  b. If k < 0, let k be 0.
//       let k = Math.max(n >= 0 ? n : len - Math.abs(n), 0);

//       function sameValueZero(x, y) {
//         return x === y || (typeof x === 'number' && typeof y === 'number' && isNaN(x) && isNaN(y));
//       }

//       // 7. Repeat, while k < len
//       while (k < len) {
//         // a. Let elementK be the result of ? Get(O, ! ToString(k)).
//         // b. If SameValueZero(searchElement, elementK) is true, return true.
//         // c. Increase k by 1.
//         if (sameValueZero(o[k], searchElement)) {
//           return true;
//         }
//         k++;
//       }

//       // 8. Return false
//       return false;
//     }
//   });
// }

const convertToSlug = (str?: string | null) => (str ? str.replace(/\s/g, '-') : '');

export const makeExternalUrl = (fragment?: string | null) => {
  if (!fragment) {
    return '';
  }

  if (fragment && fragment.includes) {
    return fragment.includes('http') ? fragment : `https://${fragment}`;
  }
  return '#';
};

export const parseDate = (dateString?: string | null) => {
  if (dateString) {
    return moment(dateString).format('MMM D[,] YYYY');
  }
  return '';
};

export const isResolvedExternalUrl = (url: string | null | undefined) => {
  const pattern = /https?:\/\//;
  return pattern.test(url || '');
};

// ** Matches the following examples
// - example-slug
// - shops/example-slug
export const isSlug = (url: string) => {
  if (isResolvedExternalUrl(url)) return false;
  const parts = url.split('/');
  const buttonSlug = parts[parts.length - 1];
  const pattern = /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/;
  return pattern.test(buttonSlug);
};

export const attachLocale = (url: string, locale: Locale = 'en') => {
  if (!url) {
    return '';
  }

  // do not attach locale to external links
  if (url.includes('http') || url.includes('https') || url.includes('www')) {
    return url;
  }

  // do not attach locale to anchor links (#)
  if (url && url.length && url[0] === '#') {
    return url;
  }

  return `/${locale}${url[0] === '/' ? '' : '/'}${url}`;
};

export const getSignOutUrl = (locale: Locale = 'en') => attachLocale('/signout', locale);

export const attachCategorySlug = (slug?: string | null) => {
  if (!slug) {
    return '';
  }

  return `/categories/${slug}`;
};

export const attachSubcategorySlug = (parentCategorySlug?: string | null, subCategorySlug?: string | null) => {
  if (!parentCategorySlug || !subCategorySlug) {
    return '';
  }

  const query = new URLSearchParams();
  query.append('subCategory', subCategorySlug);
  return `/categories/${parentCategorySlug}?${query.toString()}`;
};

export const attachShopSlug = (slug?: string | null) => {
  if (!slug) {
    return '';
  }

  return `/shops/${slug}`;
};

export const attachSeasonalSlug = (slug?: string | null) => {
  if (!slug) {
    return '';
  }

  return `/seasonal/${slug}`;
};

export const attachNotFoundSlug = (slug?: string | null) => {
  if (!slug) {
    return '';
  }

  return `/${slug}`;
};

export const makeExternalCmp = (
  campaign?: string | null, channel?: string | null, tactic?: string | null, linkItem?: string | null,
) => {
  const sadFaces = [
    convertToSlug(campaign),
    convertToSlug(channel),
    convertToSlug(tactic),
    convertToSlug(linkItem),
  ].filter((c) => c).join('-_-');
  if (sadFaces.length) {
    return `ext_cmp=${sadFaces}`;
  }
  return '';
};

export const makeInternalCmp = (
  id?: string | null, docket?: string | null, name?: string | null,
  type?: string | null, creative?: string | null,
) => {
  const sadFaces = [
    convertToSlug(id),
    convertToSlug(docket),
    convertToSlug(name),
    convertToSlug(type),
    convertToSlug(creative),
  ].filter((c) => c).join('-_-');
  if (sadFaces.length) {
    return `int_cmp=${sadFaces}`;
  }
  return '';
};

export type AnalyticsTags = {
  dataTrack?: string | null; campaign?: string | null; channel?: string | null;
  tactic?: string | null; linkItem?: string | null; internalCmpId?: string | null;
  internalCmpDocket?: string | null; internalCmpName?: string | null;
  internalCmpType?: string | null; internalCmpCreative?: string | null;
};
export const trackAnalytics = (
  parentId: string,
  componentName: string | null | undefined,
  unTrackedUrl: string | null | undefined,
  tags?: AnalyticsTags | null,
  hash?: string | null,
  slug?: string | null,
) => {
  const maybeDataTrack = (tags && tags.dataTrack) || '';
  const hashToUse = hash ? `#${hash}` : '';

  const analytics: { url: string | null | undefined; dataTrackingId: string; dataTrack: string } = {
    url: '',
    dataTrackingId: '',
    dataTrack: maybeDataTrack,
  };

  if (!tags) {
    return {
      url: `${unTrackedUrl}${hashToUse}`,
      dataTrackingId: '',
      dataTrack: maybeDataTrack,
    };
  }

  const {
    campaign,
    channel,
    tactic,
    linkItem,
  } = tags;

  const {
    internalCmpId,
    internalCmpDocket,
    internalCmpName,
    internalCmpType,
    internalCmpCreative,
  } = tags;

  const internalSadFaces = makeInternalCmp(
    internalCmpId, internalCmpDocket, internalCmpName, internalCmpType, internalCmpCreative,
  );
  const externalSadFaces = makeExternalCmp(campaign, channel, tactic, linkItem);

  let trackingUrl = '';
  // internal and external exist
  if (internalSadFaces.length && externalSadFaces.length) {
    trackingUrl = `${internalSadFaces}&${externalSadFaces}`;

    // only external exist
  } else if (internalSadFaces.length === 0 && externalSadFaces.length) {
    trackingUrl = `${externalSadFaces}`;

    // only internal exist
  } else if (internalSadFaces.length) {
    trackingUrl = `${internalSadFaces}`;
  }

  let parameterSymbol = trackingUrl.length === 0 && hashToUse.length === 0 ? '' : '?';
  if (unTrackedUrl && unTrackedUrl.length > 0 && typeof unTrackedUrl === 'string' && unTrackedUrl.indexOf('?') >= 0) {
    parameterSymbol = '&';
  }

  // Do not use makeExternalUrl because it prepends 'http' before the link,
  // conflicting with the base URL (KD Jul 10, 2017).
  analytics.url = trackingUrl && hashToUse
    ? `${unTrackedUrl}${parameterSymbol}${trackingUrl}${hashToUse}`
    : unTrackedUrl;
  analytics.dataTrackingId = `${parentId}_${slug ? `${slug}_` : ''}${componentName}`;
  return analytics;
};

export { trackAnalyticsNew } from './trackAnalyticsNew';

export const makeDomId = (domName: string, slug: string, isMobile = false) => (
  `${domName}_${slug}${isMobile ? '_mobile' : '_desktop'}`
);

export const toSlug = (text: string | null | undefined = '') => (
  text ? text.trim().replace(/\s+/g, '-').toLowerCase() : null
);

export const isValidCollectorNumber = (collectorNumber = 'invalid collector') => {
  const collectorNumberPattern = /^[0-9]*$/;
  return collectorNumberPattern.test(collectorNumber);
};

export const canUseDom = () => !!(
  (typeof window !== 'undefined'
      && window.document && window.document.createElement)
);

export const sortAlphabetically = (a: { name?: string | null } | null, b: { name?: string | null } | null) => {
  const nameA = a && a.name ? a.name.toUpperCase() : '';
  const nameB = b && b.name ? b.name.toUpperCase() : '';
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  return 0;
};

export const setWindowHash = (...hashTexts: string[]) => {
  if (!window || !hashTexts) {
    return;
  }

  window.location.hash = hashTexts
    .filter((text) => (typeof text) === 'string')
    .join('-_-');
};

export const scrollJump = (anchorId: string) => {
  if (window?.document) {
    window.document.getElementById(anchorId)?.scrollIntoView();
  }
};

// Input paramter is coming from window.location.hash
// Input starts with '#'
export const sanitizeHash = (windowHash: string) => {
  const hashData = (windowHash).replace('#', '');
  let sanitizedHash = hashData;
  const questionMarkIndex = hashData.indexOf('?');
  if (questionMarkIndex !== -1) {
    sanitizedHash = hashData.slice(0, questionMarkIndex);
  }
  return sanitizedHash;
};

export const redirectToUrl = (url: string) => {
  if (!window?.location || !url) {
    return;
  }

  window.location.href = url;
};

export const isMobile = () => {
  let width = Infinity;
  if (window?.document) {
    width = Math.max(window.document.documentElement.clientWidth, window.innerWidth || 0);
  }
  const deviceBreakpoint = theme.breakpoints.m[0];
  const thisIsMobile = width < deviceBreakpoint;
  return thisIsMobile;
};

export const replaceSpaceWithPercent20 = (string = '') => {
  const removedSpace = string.replace(/\s/g, '%20');
  return removedSpace;
};

export const isEnter = (event: { key?: string; nativeEvent?: { key?: string } }) => {
  let isEvent = false;
  if (event.key === ENTER_KEY) {
    isEvent = true;
  } else if (event.nativeEvent && event.nativeEvent.key === ENTER_KEY) {
    isEvent = true;
  }
  return isEvent;
};

export const findLetterFromShop = (shop: { displayName?: string | null; name?: string | null } | null) => {
  if (shop) {
    if (shop.displayName && shop.displayName.length) {
      return shop.displayName[0].toUpperCase();
    } if (shop.name && shop.name.length) {
      return shop.name[0].toUpperCase();
    }
  }
  return null;
};

export const switchLocale = (url = '', locale = 'en') => url.replace(/(en|fr)/, locale);

export const URL_STATE_CHANGE_EVENT = 'shops:urlchange';

export const getCurrentRelativeUrl = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

export const replaceBrowserUrl = (url: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.history.replaceState({}, '', url);
  window.dispatchEvent(new CustomEvent(URL_STATE_CHANGE_EVENT, {
    detail: {
      url: getCurrentRelativeUrl(),
    },
  }));
};

export const resolveLanguageSwitchUrl = ({
  currentPath = '',
  targetLocale = 'en',
  localeUrls = {},
  subCategoryLocaleSlugMap = {},
}: {
  currentPath?: string; targetLocale?: Locale;
  localeUrls?: Partial<Record<Locale, string>>;
  subCategoryLocaleSlugMap?: Record<string, Partial<Record<Locale, string | null>>>;
}) => {
  const baseUrl = localeUrls[targetLocale] || switchLocale(currentPath, targetLocale);

  if (!baseUrl) {
    return baseUrl;
  }

  const currentUrl = new URL(currentPath, BASE_SITE_URL);
  const selectedSubCategorySlugs = currentUrl
    .searchParams
    .getAll('subCategory')
    .filter(Boolean);
  const targetUrl = new URL(baseUrl, BASE_SITE_URL);

  targetUrl.hash = currentUrl.hash;

  if (!Object.keys(subCategoryLocaleSlugMap).length) {
    return targetUrl.pathname.includes('/categories/')
      ? `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
      : baseUrl;
  }

  if (!selectedSubCategorySlugs.length) {
    return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
  }

  const sourceSlugToTargetSlug = Object.values(subCategoryLocaleSlugMap).reduce((acc, localeSlugs) => {
    if (!localeSlugs?.[targetLocale]) {
      return acc;
    }

    Object.values(localeSlugs).forEach((sourceSlug) => {
      if (sourceSlug) {
        acc[sourceSlug] = localeSlugs[targetLocale] || '';
      }
    });

    return acc;
  }, {} as Record<string, string>);

  targetUrl.searchParams.delete('subCategory');

  selectedSubCategorySlugs
    .map((slug) => sourceSlugToTargetSlug[slug])
    .filter(Boolean)
    .forEach((slug) => targetUrl.searchParams.append('subCategory', slug));

  return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
};

export type SiteMapLink = {
  url?: string | null; name?: string | null; text?: string | null;
  navSlotPurpose?: string | null; tags?: object | null;
  descriptiveText?: string | null; dropdownEntries?: (SiteMapLink | null)[] | null;
};
export const extractSiteMapLinks = (links: SiteMapLink[] = []): {
  url: string | null; name: string | null; tags: object | null;
  navSlotPurpose: string | null; descriptiveText?: string | null;
}[] => links.reduce<{
    url: string | null; name: string | null; tags: object | null;
    navSlotPurpose: string | null; descriptiveText?: string | null;
  }[]>((results, slot) => {
    const {
      url = '',
      name = '',
      text = '',
      navSlotPurpose = '',
      tags = {},
      descriptiveText,
    } = slot;

    const link = {
      url,
      name: text || name,
      tags,
      navSlotPurpose,
      descriptiveText,
    };

    // Need to omit links to Sign In/Out and Account Nav Slot
    if (navSlotPurpose === ACCOUNT_DROPDOWN || navSlotPurpose === SIGN_IN_BUTTON) {
      return [];
    }

    results.push(link);

    if (slot.dropdownEntries && slot.dropdownEntries.length) {
      const dropdownLinks = extractSiteMapLinks(
        slot.dropdownEntries.filter((entry): entry is SiteMapLink => entry !== null),
      );
      results.push(...dropdownLinks);
    }

    return results;
  }, []);

export const genericProtocol = (url: string) => (!process.browser ? url : url.replace(/^(https?):\/\//i, '//'));

export const resolveInterstitialURI = ({ shopSlug, offerSlug = '', adSlug = '', locale = 'en' }: {
  shopSlug?: string | null; offerSlug?: string; adSlug?: string; locale?: Locale;
}) => {
  const resolvedUrl = attachLocale('/redirect', locale);
  const redirectShopSlug = shopSlug || '';
  let externalRedirectUrl = `${resolvedUrl}?shopSlug=${redirectShopSlug}`;

  if (offerSlug.length > 0) {
    externalRedirectUrl += `&offerSlug=${offerSlug}`;
  }

  if (adSlug.length > 0) {
    externalRedirectUrl += `&adSlug=${adSlug}`;
  }

  return { externalRedirectUrl };
};

export const isChrome = () => typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage;

export const isBrowser = () => typeof window !== 'undefined';

export const accessKeyHandler = (event: { which: number; stopPropagation: () => void }, handler: () => unknown) => {
  switch (event.which) {
    case 13:
    case 32: {
      event.stopPropagation();
      return handler();
    }
    default:
      return false;
  }
};

const getPrep = (prep: string, key: string): string => {
  let p: unknown = prep;
  try {
    p = JSON.parse(prep);
  } catch {
    p = prep;
  }
  const value: unknown = get(p, [key], get(p, ['0', key], ''));
  return String(value);
};

export const resolveAccessibleLabel = (
  overrideText: string | null | undefined,
  shop: { displayName?: string | null } | null | undefined,
  p: string,
  pk1: string | null | undefined,
  actionText: string | null | undefined,
  label: string | null | undefined,
  pk2: string | null | undefined,
) => {
  if (overrideText) {
    return overrideText;
  }

  if (actionText) {
    if (shop && shop.displayName) {
      const suffix = label && pk2 ? [getPrep(p, pk2), label].join(' ') : '';
      return `${actionText} ${getPrep(p, pk1 || 'at1')} ${shop.displayName} ${suffix}`;
    }
    return actionText;
  }
  return null;
};

export const screenReaderTextReplace = (text: string | null | undefined, locale: Locale, pad = true) => {
  const times = {
    en: ' times',
    fr: ' fois',
  };
  const pattern = pad ? / (\d+)x / : /(\d+)x/;
  const replacement = (times[locale] ? times[locale] : 'x') + (pad ? ' ' : '');
  return text && text
    .replace(pattern, `${pad ? ' ' : ''}$1${replacement}`)
    .replace(/\*/g, '');
};

export const extensionRedirect = (slug: string | null | undefined, environment = 'production') => {
  if (!slug) {
    return;
  }

  const cookieName = `${EXTENSION_REDIRECT_COOKIE_PREFIX}-${environment}`;

  const retailerRedirects: Record<string, unknown> = (() => {
    try {
      const parsed: unknown = JSON.parse(Cookies.get(cookieName) || '{}');
      return isRecord(parsed) ? parsed : {};
    } catch (err) {
      console.error(err);
      return {};
    }
  })();

  retailerRedirects[slug] = moment().unix();

  const cookieOptions = {
    path: '/',
    secure: Boolean(COOKIE_SECURE),
  };
  Cookies.set(cookieName, JSON.stringify(retailerRedirects), cookieOptions);
};

export const getAnchorButtonUrlProps = (
  buttonUrl: string | null | undefined,
  locale: Locale,
) => {
  let directLink = false;
  let resolvedUrl = null;

  if (buttonUrl && isSlug(buttonUrl)) {
    resolvedUrl = attachLocale(buttonUrl, locale);
  } else {
    resolvedUrl = makeExternalUrl(buttonUrl);
    directLink = true;
  }

  return {
    url: resolvedUrl,
    directLink,
  };
};

export const hasTargetingConsentForPPID = () => {
  const optanonConsentCookie = Cookies.get('OptanonConsent');

  if (!optanonConsentCookie) {
    return false;
  }

  try {
    const consentParams = new URLSearchParams(optanonConsentCookie);
    const groupsValue = consentParams.get('groups') || '';
    return /(^|,)C0004:1($|,)/.test(groupsValue);
  } catch {
    return false;
  }
};

export const getInitAdSlotsScript = (pageAds: unknown[], ppid = '') => {
  const ads = pageAds.filter((ad): ad is {
  adSlot: { sizeMappings: string }; active?: boolean | null; path?: string | null;
  id?: string | null; targetingPosition?: number | null;
  } => !!ad && typeof ad === 'object' && 'adSlot' in ad && !!ad.adSlot
    && typeof ad.adSlot === 'object' && 'sizeMappings' in ad.adSlot
    && typeof ad.adSlot.sizeMappings === 'string');
  const definedSlots = ads.map((ad, index) => {
    // Fallback for size mappings if JSON parsing fails or sizeMappings is missing
    let sizeMappings: unknown;
    try {
      sizeMappings = JSON.parse(ad.adSlot.sizeMappings);
    } catch (error) {
      console.error('Error parsing size mappings for ad:', ad, error);
      sizeMappings = [];
    }

    // Check if required fields (path, id, sizeMappings) exist
    if (!ad.active || !ad.path || !ad.id || !Array.isArray(sizeMappings) || !sizeMappings.length) {
      console.warn('Missing path, id, or size mappings for ad');
      return ''; // Skip this ad slot if data is incomplete
    }

    const mappings: [unknown, unknown][] = sizeMappings
      .filter((pair: unknown): pair is [unknown, unknown] => Array.isArray(pair) && pair.length >= 2);
    const flatSizeMapping = mappings.map(([, size]) => size);

    // Create the output string for this slot with the responsive mapping
    const outputString = `
      const responsiveAdSlot${index} = googletag
        .defineSlot(
          '${ad.path}',
          ${JSON.stringify(flatSizeMapping)},
          '${ad.id}'
        )
        .addService(googletag.pubads());

        const mapping${index} = googletag
          .sizeMapping()
          ${mappings
    .map(([media, size]) => (
      `.addSize(${JSON.stringify(media)}, ${JSON.stringify(size)})`
    ))
    .join('')}
          .build();

        responsiveAdSlot${index}.defineSizeMapping(mapping${index});
        ${Number.isInteger(ad.targetingPosition)
    ? `responsiveAdSlot${index}.setConfig({ targeting: { pos: '${ad.targetingPosition}' } });`
    : ''
}

    `;

    return outputString;
  }).filter(Boolean); // Filter out empty strings from invalid ads

  if (!definedSlots.length) {
    return '';
  }

  return `
    window.googletag = window.googletag || { cmd: [] };

    googletag.cmd.push(function() {
      ${definedSlots.join('')}
      ${ppid && hasTargetingConsentForPPID()
    ? `googletag.pubads().setPublisherProvidedId('${ppid}');`
    : ''
}
      googletag.enableServices();

      window.wasGoogletagInitialized = true;
      document.dispatchEvent(new Event('googletagInitialized'));
    })
  `;
};

export const getSignInUrl = (locale = 'en') => {
  const url = new URL(`/${locale}/signin`, BASE_SITE_URL);

  return url.toString();
};

export const getSigninUrlWithOrigin = ({
  currentPath,
  signInUrl,
}: { currentPath?: string | null; signInUrl: string }) => {
  const url = new URL(signInUrl, BASE_SITE_URL);

  if (currentPath) {
    let relativePath = '';

    try {
      const parsed = new URL(currentPath, BASE_SITE_URL);

      // Ensure it's same-origin before using pathname
      if (parsed.origin === BASE_SITE_URL) {
        relativePath = parsed.pathname + parsed.search + parsed.hash;
      }
    } catch {
      // If it's not a valid absolute URL, treat it as a relative one
      relativePath = currentPath;
    }
    // Only include if path is safe
    const isSafeRelativePath = relativePath.startsWith('/')
      && !relativePath.startsWith('//')
      && !relativePath.includes('://')
      && !relativePath.toLowerCase().startsWith('/javascript:')
      && !relativePath.includes('\\');

    if (isSafeRelativePath) {
      url.searchParams.set('origin', relativePath);
    }
  }

  return url.toString();
};

export const formatNumber = (value: number | string, delimiter = ',') => (
  value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, delimiter)
);
